import { useQuery } from '@apollo/client';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { AppState } from '../../lib/store';
import { RescuePortalAccessData, belongsInRescuePortal, rescuePortalAccessQuery } from '../../lib/utils';

/**
 * Renders `children` unless the current user belongs in the new rescue portal, in which case they
 * are redirected to `to` (typically a matching portal page). Used to move rescue traffic off the
 * legacy chip-management flow.
 *
 * The query uses `errorPolicy: 'all'` so a backend hiccup never blocks the underlying page from
 * rendering — non-redirected users see the legacy page as before.
 */
export default function RedirectToRescuePortal({ to, children }: { to: string; children: ReactNode }) {
  const isLoggedIn = useSelector((state: AppState) => !!state.session);
  const { data, loading } = useQuery<RescuePortalAccessData>(rescuePortalAccessQuery, {
    skip: !isLoggedIn,
    errorPolicy: 'all',
  });

  if (isLoggedIn && loading) {
    return null;
  }

  if (belongsInRescuePortal(data)) {
    return <Redirect to={to} />;
  }

  return <>{children}</>;
}
